import { default as React, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { Alert } from '@material-ui/lab';
import {
  useApi,
  configApiRef,
  alertApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';

interface ManageOwnersModalProps {
  open: boolean;
  onClose: () => void;
  appName: string;
  entityKind: string;
  entityNamespace: string;
  entityName: string;
  currentOwners: string[];
  currentUserEmail: string;
  createdBy: string;
  onOwnersUpdated: (newOwners: string[]) => void;
}

export const ManageOwnersModal = ({
  open,
  onClose,
  appName,
  entityKind,
  entityNamespace,
  entityName,
  currentOwners,
  currentUserEmail,
  createdBy,
  onOwnersUpdated,
}: ManageOwnersModalProps) => {
  const config = useApi(configApiRef);
  const alertApi = useApi(alertApiRef);
  const identityApi = useApi(identityApiRef);

  const [owners, setOwners] = useState<string[]>(currentOwners);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAddOwner = () => {
    const input = newOwnerEmail.trim();
    if (!input) {
      setError('Please enter at least one email address');
      return;
    }

    // Split by comma to support multiple emails
    const emailsToAdd = input
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(e => e);

    const validEmails: string[] = [];
    const invalidEmails: string[] = [];
    const duplicateEmails: string[] = [];
    const invalidDomainEmails: string[] = [];

    const currentOwnersLower = owners.map(o => o.toLowerCase());

    for (const email of emailsToAdd) {
      // Validate email format
      if (!emailRegex.test(email)) {
        invalidEmails.push(email);
        continue;
      }

      // Validate email domain - only @wellsky.com allowed
      if (!email.endsWith('@wellsky.com')) {
        invalidDomainEmails.push(email);
        continue;
      }

      // Check if already in list
      if (currentOwnersLower.includes(email) || validEmails.includes(email)) {
        duplicateEmails.push(email);
        continue;
      }

      validEmails.push(email);
    }

    // Build error message if there are any issues
    const errorMessages: string[] = [];
    if (invalidEmails.length > 0) {
      errorMessages.push(`Invalid format: ${invalidEmails.join(', ')}`);
    }
    if (invalidDomainEmails.length > 0) {
      errorMessages.push(
        `Non-WellSky emails: ${invalidDomainEmails.join(', ')}`,
      );
    }
    if (duplicateEmails.length > 0) {
      errorMessages.push(`Already in list: ${duplicateEmails.join(', ')}`);
    }

    // Add valid emails
    if (validEmails.length > 0) {
      setOwners([...owners, ...validEmails]);
      setNewOwnerEmail('');

      // Show success message with any warnings
      if (errorMessages.length > 0) {
        setError(
          `Added ${validEmails.length} email(s). Issues: ${errorMessages.join(
            '; ',
          )}`,
        );
      } else {
        setError(null);
      }
    } else {
      // No valid emails - show error
      if (errorMessages.length > 0) {
        setError(errorMessages.join('; '));
      } else {
        setError('No valid emails to add');
      }
    }
  };

  const handleRemoveOwner = (emailToRemove: string) => {
    // Prevent removing the original creator
    if (createdBy && emailToRemove.toLowerCase() === createdBy.toLowerCase()) {
      setError(
        `Cannot remove original creator (${createdBy}). Original owner must always retain access.`,
      );
      return;
    }

    // Prevent removing yourself if you're the only owner
    if (
      owners.length === 1 &&
      owners[0].toLowerCase() === currentUserEmail.toLowerCase()
    ) {
      setError('Cannot remove the last owner. At least one owner is required.');
      return;
    }

    setOwners(
      owners.filter(
        email => email.toLowerCase() !== emailToRemove.toLowerCase(),
      ),
    );
    setError(null);
  };

  const handleSave = async () => {
    // Validate at least one owner
    if (owners.length === 0) {
      setError('At least one owner is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const backendUrl = config.getString('backend.baseUrl');
      const endpoint = `${backendUrl}/api/microapps-api/update-owners`;

      // Get authentication token
      const { token } = await identityApi.getCredentials();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: 'include',
        body: JSON.stringify({
          appName,
          entityKind,
          entityNamespace,
          entityName,
          owners: owners.join(', '),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to update owners: ${response.statusText}`,
        );
      }

      // Success - notify parent and close
      alertApi.post({
        message: 'Owners updated successfully.',
        severity: 'success',
      });

      onOwnersUpdated([...owners]);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update owners');
      alertApi.post({
        message: `Failed to update owners: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original owners
    setOwners(currentOwners);
    setNewOwnerEmail('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Owners - {appName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Owners can redeploy and unregister this application. Only @wellsky.com
          email addresses are allowed.
        </Typography>

        {error && (
          <Alert severity="error" style={{ marginTop: 16, marginBottom: 16 }}>
            {error}
          </Alert>
        )}

        <Box mt={2} mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Current Owners
          </Typography>
          <List>
            {owners.map((email, index) => {
              const isOriginalCreator = Boolean(
                createdBy && email.toLowerCase() === createdBy.toLowerCase(),
              );
              return (
                <ListItem key={index}>
                  <ListItemText
                    primary={email}
                    secondary={
                      isOriginalCreator
                        ? 'Original Creator (cannot be removed)'
                        : undefined
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleRemoveOwner(email)}
                      disabled={loading || isOriginalCreator}
                      title={
                        isOriginalCreator
                          ? 'Original creator cannot be removed'
                          : 'Remove owner'
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
            {owners.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No owners"
                  secondary="Add at least one owner"
                  primaryTypographyProps={{ color: 'error' }}
                />
              </ListItem>
            )}
          </List>
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            fullWidth
            label="Add owner email(s)"
            variant="outlined"
            size="small"
            value={newOwnerEmail}
            onChange={e => setNewOwnerEmail(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddOwner();
              }
            }}
            disabled={loading}
            placeholder="user1@wellsky.com, user2@wellsky.com"
            helperText="Enter one or more emails separated by commas"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddOwner}
            disabled={loading || !newOwnerEmail.trim()}
          >
            Add
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginTop: 8, display: 'block' }}
        >
          Changes are saved to GitHub and the catalog is refreshed
          automatically.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || owners.length === 0}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
